/**
 * Personas: the contract shared by the API routes, the product UI and the free
 * prompt generator.
 *
 * A persona is a synthetic asker a discovery prompt can be asked AS: a person
 * asking for themselves, their household or someone they care for
 * (`askerType: "individual"`), or someone asking on behalf of an organization
 * (`"professional"`). Its structured fields pre-answer the qualifying
 * questions assistants ask before they recommend anything;
 * `renderPersonaContext` (utils/persona-context) turns them into the
 * deterministic first-person text we send.
 *
 * This file holds:
 *  - the zod request schemas (API input: limits are enforced here, not by an
 *    LLM schema);
 *  - `normalizePersonaFields`, the one normalizer every write path uses
 *    (whitespace collapsed, empty strings to null, lists de-duplicated and
 *    capped, enumerated values validated);
 *  - the wire shapes (`PersonaDTO`, `PersonaDraft`) with ISO-string dates.
 *
 * Enumerated fields are stored as plain strings in Postgres and validated
 * against the persona-context constants, so adding a value never needs a
 * migration.
 *
 * Pure types, constants and zod: safe for `@repo/core/client`.
 */

import { z } from "zod";
import {
	PERSONA_ASKER_TYPES,
	PERSONA_COMPANY_SIZES,
	PERSONA_CONTEXT_MAX_CHARS,
	PERSONA_DECISION_ROLES,
	PERSONA_ROLE_GROUPS,
	PERSONA_SENIORITIES,
	type PersonaAskerType,
	type PersonaCompanySize,
	type PersonaDecisionRole,
	type PersonaRoleGroup,
	type PersonaSeniority,
} from "../utils/persona-context";

/* ------------------------------------------------------------------ */
/* Limits and vocabularies                                             */
/* ------------------------------------------------------------------ */

/** Optional user-added display name. Never generated, never sent to AI. */
export const PERSONA_NAME_MAX_CHARS = 60;
export const PERSONA_TITLE_MAX_CHARS = 80;
/** segment, industry, region, generatedFromSegment. */
export const PERSONA_SHORT_TEXT_MAX_CHARS = 120;
/** An individual's one-sentence `household`. */
export const PERSONA_HOUSEHOLD_MAX_CHARS = 200;
/** techStack, priorities, constraints: items per list. */
export const PERSONA_LIST_MAX_ITEMS = 8;
/** techStack, priorities, constraints: characters per item. */
export const PERSONA_LIST_ITEM_MAX_CHARS = 120;
/** Same cap as the rendered context: an override is sent instead of it. */
export const PERSONA_OVERRIDE_MAX_CHARS = PERSONA_CONTEXT_MAX_CHARS;
/** Personas one bulk-create request may carry. */
export const PERSONA_BULK_CREATE_MAX = 20;

/**
 * `PromptRun.runType` of a daily run asked AS a persona (`personaId` set).
 * Every "nightly" reader excludes it by run type, so headline metrics never
 * move when personas are added. Persona runs are answer-only: no citations,
 * no fan-out harvest.
 */
export const PERSONA_RUN_TYPE = "nightly_persona";

/** Where a persona came from. "imported" = the free-tool claim flow. */
export const PERSONA_SOURCES = ["generated", "manual", "imported"] as const;
export type PersonaSource = (typeof PERSONA_SOURCES)[number];

/**
 * Draft generation modes (the generate dialog's "Who to draft" step):
 *  - "mix" (the default): 3 to 5 different people who would each ask the
 *    property's questions, across its audience segments;
 *  - "one": one persona, the most typical asker in an optional segment;
 *  - "committee": 3 to 5 professionals at ONE organization weighing the same
 *    decision (a buying committee). Not offered when the audience is "people".
 */
export const PERSONA_DRAFT_MODES = ["one", "mix", "committee"] as const;
export type PersonaDraftMode = (typeof PERSONA_DRAFT_MODES)[number];
export const PERSONA_DEFAULT_DRAFT_MODE: PersonaDraftMode = "mix";

/**
 * Size bounds of a "mix" or "committee" draft. A group that survives
 * validation with fewer is still returned.
 */
export const PERSONA_PANEL_MIN = 3;
export const PERSONA_PANEL_MAX = 5;

/**
 * Who asks AI about the property. Lives on the brand profile (BrandSnapshot
 * data, top-level `audience`): "people" ask for themselves or their
 * households, "organizations" ask on behalf of a business or institution,
 * "both" mixes them. It decides which asker types the generator drafts and
 * whether "committee" mode is allowed.
 */
export const PERSONA_AUDIENCES = ["people", "organizations", "both"] as const;
export type PersonaAudience = (typeof PERSONA_AUDIENCES)[number];

/**
 * Where the effective audience came from: the snapshot extractor
 * ("profile"), the first persona generation ("inferred"), a user edit
 * ("user"), or nowhere yet (null).
 */
export type PersonaAudienceSource = "profile" | "inferred" | "user";

/** `GET /personas/by-property/:propertyId/audience` and the list response. */
export interface PersonaAudienceInfo {
	audience: PersonaAudience | null;
	/** One sentence naming the main kinds of people who ask. */
	audienceSummary: string | null;
	source: PersonaAudienceSource | null;
}

/** Whether a draft mode is allowed for an audience ("committee" needs organizations). */
export function personaDraftModeAllowed(
	mode: PersonaDraftMode,
	audience: PersonaAudience | null | undefined,
): boolean {
	return !(mode === "committee" && audience === "people");
}

export type PersonaProjectStatus = "ACTIVE" | "COMPLETED" | "ARCHIVED";

function isOneOf<T extends string>(
	values: readonly T[],
	value: unknown,
): value is T {
	return (
		typeof value === "string" &&
		(values as readonly string[]).includes(value)
	);
}

export function isPersonaAskerType(value: unknown): value is PersonaAskerType {
	return isOneOf(PERSONA_ASKER_TYPES, value);
}
export function isPersonaAudience(value: unknown): value is PersonaAudience {
	return isOneOf(PERSONA_AUDIENCES, value);
}
export function isPersonaRoleGroup(value: unknown): value is PersonaRoleGroup {
	return isOneOf(PERSONA_ROLE_GROUPS, value);
}
export function isPersonaSeniority(value: unknown): value is PersonaSeniority {
	return isOneOf(PERSONA_SENIORITIES, value);
}
export function isPersonaDecisionRole(
	value: unknown,
): value is PersonaDecisionRole {
	return isOneOf(PERSONA_DECISION_ROLES, value);
}
export function isPersonaCompanySize(
	value: unknown,
): value is PersonaCompanySize {
	return isOneOf(PERSONA_COMPANY_SIZES, value);
}
export function isPersonaSource(value: unknown): value is PersonaSource {
	return isOneOf(PERSONA_SOURCES, value);
}

/* ------------------------------------------------------------------ */
/* Request schemas (API input)                                         */
/* ------------------------------------------------------------------ */

const optionalText = (max: number) =>
	z.string().trim().max(max).nullable().optional();

const personaListSchema = z
	.array(z.string().trim().max(PERSONA_LIST_ITEM_MAX_CHARS))
	.max(PERSONA_LIST_MAX_ITEMS)
	.optional();

/**
 * The editable persona fields, as the API accepts them. `askerType` defaults
 * to "professional". A professional needs a `roleGroup`; an individual has
 * none, and its organization fields (roleGroup, seniority, decisionRole,
 * companySize, industry) are stored as null whatever is sent. `household` is
 * kept for individuals only. Every other field is optional; `null` clears a
 * field, an empty string is read as null. Lists are at most 8 items of at
 * most 120 characters (blank items are dropped). The override is at most 600
 * characters: the editor caps what it sends, since the renderer never sends
 * more than that anyway.
 */
export const PersonaFieldsSchema = z.object({
	askerType: z.enum(PERSONA_ASKER_TYPES).optional(),
	name: optionalText(PERSONA_NAME_MAX_CHARS),
	title: optionalText(PERSONA_TITLE_MAX_CHARS),
	roleGroup: z.enum(PERSONA_ROLE_GROUPS).nullable().optional(),
	seniority: z.enum(PERSONA_SENIORITIES).nullable().optional(),
	decisionRole: z.enum(PERSONA_DECISION_ROLES).nullable().optional(),
	segment: optionalText(PERSONA_SHORT_TEXT_MAX_CHARS),
	companySize: z.enum(PERSONA_COMPANY_SIZES).nullable().optional(),
	industry: optionalText(PERSONA_SHORT_TEXT_MAX_CHARS),
	region: optionalText(PERSONA_SHORT_TEXT_MAX_CHARS),
	household: optionalText(PERSONA_HOUSEHOLD_MAX_CHARS),
	techStack: personaListSchema,
	priorities: personaListSchema,
	constraints: personaListSchema,
	contextOverride: optionalText(PERSONA_OVERRIDE_MAX_CHARS),
});
export type PersonaFieldsInput = z.infer<typeof PersonaFieldsSchema>;

/** `PATCH /personas/item/:personaId`: any subset of the fields. */
export const PersonaUpdateSchema = PersonaFieldsSchema.partial();
export type PersonaUpdateInput = z.infer<typeof PersonaUpdateSchema>;

/** `POST /personas/by-property/:propertyId`: bulk create (reviewed drafts or one manual persona). */
export const PersonaCreateRequestSchema = z.object({
	personas: z.array(PersonaFieldsSchema).min(1).max(PERSONA_BULK_CREATE_MAX),
	source: z.enum(["generated", "manual"]),
	generatedFromSegment: optionalText(PERSONA_SHORT_TEXT_MAX_CHARS),
});
export type PersonaCreateRequest = z.infer<typeof PersonaCreateRequestSchema>;

/** `POST /personas/by-property/:propertyId/drafts`: generate drafts (never persisted). */
export const PersonaDraftsRequestSchema = z.object({
	mode: z.enum(PERSONA_DRAFT_MODES).default(PERSONA_DEFAULT_DRAFT_MODE),
	/**
	 * "one" / "committee": the audience segment to draft for (one of the
	 * brand profile's `icp.segments`, or free text). "mix" ignores it.
	 */
	segment: z
		.string()
		.trim()
		.min(1)
		.max(PERSONA_SHORT_TEXT_MAX_CHARS)
		.optional(),
});
export type PersonaDraftsRequest = z.infer<typeof PersonaDraftsRequestSchema>;

/** `PUT /personas/by-property/:propertyId/audience`: a user edit of the audience. */
export const PersonaAudienceUpdateSchema = z.object({
	audience: z.enum(PERSONA_AUDIENCES),
});
export type PersonaAudienceUpdate = z.infer<typeof PersonaAudienceUpdateSchema>;

/* ------------------------------------------------------------------ */
/* Normalized fields                                                   */
/* ------------------------------------------------------------------ */

/**
 * Every persona field, fully populated: what the service stores and what the
 * wire shapes carry. Structurally a `PersonaFields` (utils/persona-context),
 * so it feeds `renderPersonaContext` and the display helpers directly.
 * Individuals always have null roleGroup, seniority, decisionRole,
 * companySize and industry; professionals always have a roleGroup and a
 * null household.
 */
export interface PersonaFieldValues {
	askerType: PersonaAskerType;
	name: string | null;
	title: string | null;
	roleGroup: PersonaRoleGroup | null;
	seniority: PersonaSeniority | null;
	decisionRole: PersonaDecisionRole | null;
	segment: string | null;
	companySize: PersonaCompanySize | null;
	industry: string | null;
	region: string | null;
	household: string | null;
	techStack: string[];
	priorities: string[];
	constraints: string[];
	contextOverride: string | null;
}

/** Loose input for `normalizePersonaFields`: API input, stored rows, or LLM output. */
export interface PersonaFieldsLike {
	askerType?: string | null;
	name?: string | null;
	title?: string | null;
	roleGroup?: string | null;
	seniority?: string | null;
	decisionRole?: string | null;
	segment?: string | null;
	companySize?: string | null;
	industry?: string | null;
	region?: string | null;
	household?: string | null;
	techStack?: readonly (string | null | undefined)[] | null;
	priorities?: readonly (string | null | undefined)[] | null;
	constraints?: readonly (string | null | undefined)[] | null;
	contextOverride?: string | null;
}

/** Collapse whitespace runs (newlines included) to one space and trim. */
export function collapsePersonaWhitespace(value: string): string {
	return value.replace(/\s+/g, " ").trim();
}

/** Cut at the last word boundary that fits (no trailing punctuation dangling). */
export function capPersonaText(value: string, max: number): string {
	if (value.length <= max) {
		return value;
	}
	const cut = value.slice(0, max);
	// The cut already ends on a whole word when the next character is a space.
	const lastSpace = value[max] === " " ? max : cut.lastIndexOf(" ");
	const base = lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut;
	return base.replace(/[\s,;:–—-]+$/, "");
}

function cleanText(value: unknown, max: number): string | null {
	if (typeof value !== "string") {
		return null;
	}
	const text = capPersonaText(collapsePersonaWhitespace(value), max);
	return text.length > 0 ? text : null;
}

function cleanList(value: unknown): string[] {
	if (!Array.isArray(value)) {
		return [];
	}
	const out: string[] = [];
	const seen = new Set<string>();
	for (const item of value) {
		const text = cleanText(item, PERSONA_LIST_ITEM_MAX_CHARS);
		if (!text) {
			continue;
		}
		const key = text.toLowerCase();
		if (seen.has(key)) {
			continue;
		}
		seen.add(key);
		out.push(text);
		if (out.length >= PERSONA_LIST_MAX_ITEMS) {
			break;
		}
	}
	return out;
}

/**
 * Lenient enum read: case-insensitive, spaces and hyphens read as
 * underscores ("Economic buyer" → "economic_buyer"). Company sizes also fold
 * dashes and thousands separators ("1,001–5,000" → "1001-5000").
 */
function cleanEnum<T extends string>(
	values: readonly T[],
	value: unknown,
	kind: "key" | "size" = "key",
): T | null {
	if (typeof value !== "string") {
		return null;
	}
	const raw = value.trim().toLowerCase();
	const folded =
		kind === "size"
			? raw.replace(/[–—]/g, "-").replace(/[,\s]/g, "")
			: raw.replace(/[\s-]+/g, "_");
	return isOneOf(values, folded) ? folded : null;
}

export interface NormalizePersonaFieldsOptions {
	/**
	 * Role group for a professional that has none (generated drafts). Without
	 * it, a professional with no valid role group is rejected.
	 */
	defaultRoleGroup?: PersonaRoleGroup;
}

/**
 * Normalize persona fields for storage and rendering. A missing or unknown
 * `askerType` reads as "professional".
 *
 * - Individual: roleGroup, seniority, decisionRole, companySize and industry
 *   are forced to null; `household` is kept.
 * - Professional: returns null when the role group is missing or unknown
 *   (unless `defaultRoleGroup` is given); `household` is forced to null.
 *
 * Unknown values of the other enumerated fields become null; free text is
 * collapsed to one line and capped; lists drop blanks and duplicates
 * (case-insensitive) and keep at most 8 items.
 */
export function normalizePersonaFields(
	input: PersonaFieldsLike,
	options: NormalizePersonaFieldsOptions = {},
): PersonaFieldValues | null {
	const askerType: PersonaAskerType =
		cleanEnum(PERSONA_ASKER_TYPES, input.askerType) ?? "professional";
	const common = {
		name: cleanText(input.name, PERSONA_NAME_MAX_CHARS),
		title: cleanText(input.title, PERSONA_TITLE_MAX_CHARS),
		segment: cleanText(input.segment, PERSONA_SHORT_TEXT_MAX_CHARS),
		region: cleanText(input.region, PERSONA_SHORT_TEXT_MAX_CHARS),
		techStack: cleanList(input.techStack),
		priorities: cleanList(input.priorities),
		constraints: cleanList(input.constraints),
		contextOverride: cleanText(
			input.contextOverride,
			PERSONA_OVERRIDE_MAX_CHARS,
		),
	};
	if (askerType === "individual") {
		return {
			askerType,
			...common,
			roleGroup: null,
			seniority: null,
			decisionRole: null,
			companySize: null,
			industry: null,
			household: cleanText(input.household, PERSONA_HOUSEHOLD_MAX_CHARS),
		};
	}
	const roleGroup =
		cleanEnum(PERSONA_ROLE_GROUPS, input.roleGroup) ??
		options.defaultRoleGroup ??
		null;
	if (!roleGroup) {
		return null;
	}
	return {
		askerType,
		...common,
		roleGroup,
		seniority: cleanEnum(PERSONA_SENIORITIES, input.seniority),
		decisionRole: cleanEnum(PERSONA_DECISION_ROLES, input.decisionRole),
		companySize: cleanEnum(
			PERSONA_COMPANY_SIZES,
			input.companySize,
			"size",
		),
		industry: cleanText(input.industry, PERSONA_SHORT_TEXT_MAX_CHARS),
		household: null,
	};
}

/* ------------------------------------------------------------------ */
/* Wire shapes                                                         */
/* ------------------------------------------------------------------ */

/** A project a persona is assigned to (the ProjectPersona link). */
export interface PersonaProjectRef {
	id: string;
	name: string | null;
	status: PersonaProjectStatus;
	/** When the persona was added to the project (ISO). Billing's trackedSince. */
	assignedAt: string;
}

/**
 * A stored persona, as every route returns it. Dates are ISO strings.
 *
 * Archiving keeps the project links: `projects` still lists them, and every
 * reader that decides what runs filters `archivedAt: null` itself.
 */
export interface PersonaDTO extends PersonaFieldValues {
	id: string;
	/** The owning (parent) property. */
	propertyId: string;
	/** Exactly what is sent in front of each prompt (at most 600 characters). */
	contextText: string;
	/** personaContextHash(contextText). */
	contextHash: string;
	/** Starts at 1; bumps whenever `contextText` changes. */
	version: number;
	source: PersonaSource;
	generatedFromSegment: string | null;
	archivedAt: string | null;
	createdAt: string;
	updatedAt: string;
	/** Every project the persona is assigned to, any status, oldest link first. */
	projects: PersonaProjectRef[];
}

/**
 * A generated, unsaved persona (the generate dialog's review step and the free
 * prompt generator's panel). Never has a name. `contextText` is rendered at
 * generation time for previews; the create route re-renders from the fields,
 * so an edited draft never sends stale text.
 */
export interface PersonaDraft extends Omit<PersonaFieldValues, "name"> {
	name: null;
	/**
	 * The segment the draft was generated for: the requested segment ("one",
	 * "committee") or the persona's own segment ("mix"). Null when none.
	 */
	generatedFromSegment: string | null;
	/** One line on why this persona asks these questions, shown on the draft card. */
	rationale: string | null;
	contextText: string;
}

/**
 * The result of one draft generation: the drafts plus who asks overall.
 * `audience` is the effective audience the drafts followed (the brand
 * profile's, or the one the generator inferred when the profile had none).
 */
export interface PersonaDraftGeneration {
	drafts: PersonaDraft[];
	audience: PersonaAudience;
	/** One sentence naming the main kinds of people who ask. */
	audienceSummary: string | null;
}

/** `POST /personas/by-property/:propertyId/drafts`. */
export type PersonaDraftsResponse = PersonaDraftGeneration;

/**
 * Parses a stored draft (e.g. `PromptGeneration.personaDrafts` JSON) back into
 * a `PersonaDraft`. Lenient on purpose; run `normalizePersonaFields` on the
 * result before storing it as a persona.
 */
export const PersonaDraftSchema = z.object({
	// Drafts stored before asker types have none: they are professionals.
	askerType: z.enum(PERSONA_ASKER_TYPES).default("professional"),
	name: z.null().optional(),
	title: z.string().nullable(),
	roleGroup: z.enum(PERSONA_ROLE_GROUPS).nullable(),
	seniority: z.enum(PERSONA_SENIORITIES).nullable(),
	decisionRole: z.enum(PERSONA_DECISION_ROLES).nullable(),
	segment: z.string().nullable(),
	companySize: z.enum(PERSONA_COMPANY_SIZES).nullable(),
	industry: z.string().nullable(),
	region: z.string().nullable(),
	household: z.string().nullable().default(null),
	techStack: z.array(z.string()),
	priorities: z.array(z.string()),
	constraints: z.array(z.string()),
	contextOverride: z.string().nullable(),
	generatedFromSegment: z.string().nullable(),
	rationale: z.string().nullable(),
	contextText: z.string(),
});

/** `DELETE /personas/item/:personaId`. */
export interface ArchivePersonaResponse {
	/** ISO. Unchanged when the persona was already archived. */
	archivedAt: string;
	/** ACTIVE projects the persona is on: their persona runs stop. */
	projects: PersonaProjectRef[];
	persona: PersonaDTO;
}

/** `POST /personas/item/:personaId/restore`. */
export interface RestorePersonaResponse {
	/** ACTIVE projects the persona is on: their persona runs resume. */
	projects: PersonaProjectRef[];
	persona: PersonaDTO;
}

/** `PATCH /personas/item/:personaId`. */
export interface UpdatePersonaResponse {
	persona: PersonaDTO;
	/** True when `contextText` changed (the version bumped and projects were annotated). */
	contextChanged: boolean;
}
